import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Film, Plus, Pencil, Trash2, X, Save, Upload } from 'lucide-react';

const VideoManager = () => {
  const { api } = useAuth();
  const [videos, setVideos] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', video_url: '', category: '', tags: '', quality: '1080p', duration: '', thumbnail: '', uploader: '', is_new: false, is_trending: false });
  const [categories, setCategories] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const load = () => {
    api.get('/admin/videos?limit=100').then(r => setVideos(r.data.videos || [])).catch(console.error);
    api.get('/admin/categories').then(r => setCategories(r.data || [])).catch(console.error);
  };

  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setForm({ title: '', description: '', video_url: '', category: '', tags: '', quality: '1080p', duration: '', thumbnail: '', uploader: '', is_new: false, is_trending: false });
    setEditing(null); setShowForm(false); setUploadProgress(0);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 500 * 1024 * 1024) { alert('Max 500MB'); return; }

    setUploading(true); setUploadProgress(0);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await api.post('/admin/upload/video', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (p) => setUploadProgress(Math.round((p.loaded * 100) / p.total))
      });
      setForm(prev => ({ ...prev, video_url: res.data.url }));
    } catch (err) { alert('Upload failed'); }
    setUploading(false);
  };

  const handleThumbUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post('/admin/upload/thumbnail', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(prev => ({ ...prev, thumbnail: res.data.url }));
    } catch (err) { alert('Thumbnail upload failed'); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.video_url) { alert('Please upload a video file first'); return; }
    const payload = { ...form, tags: typeof form.tags === 'string' ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : form.tags };
    try {
      if (editing) { await api.put(`/admin/videos/${editing}`, payload); }
      else { await api.post('/admin/videos', payload); }
      resetForm(); load();
    } catch (err) { alert(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : JSON.stringify(err.response?.data) || 'Error saving video'); }
  };

  const handleEdit = (v) => {
    setForm({
      title: v.title || '', description: v.description || '',
      video_url: v.video_url || '', category: v.category || '',
      tags: (v.tags || []).join(', '), quality: v.quality || '1080p',
      duration: v.duration || '', thumbnail: v.thumbnail || '', uploader: v.uploader || ''
    });
    setEditing(v.id); setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this video?')) return;
    await api.delete(`/admin/videos/${id}`); load();
  };

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2 className="section-title"><Film size={20} /> Video Management</h2>
        <button className="btn-primary" onClick={() => { resetForm(); setShowForm(true); }}><Plus size={16} /> Add Video</button>
      </div>

      {showForm && (
        <div className="admin-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 className="card-subtitle" style={{ margin: 0 }}>{editing ? 'Edit Video' : 'Add New Video'}</h3>
            <button className="btn-sm btn-delete" onClick={resetForm}><X size={14} /></button>
          </div>

          <form onSubmit={handleSave}>
            {/* Video Upload */}
            <div className="admin-card" style={{ background: 'rgba(245,158,11,0.03)', borderColor: 'rgba(245,158,11,0.1)' }}>
              {form.video_url ? (
                <div>
                  <p style={{ color: '#4caf50', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>✅ Video uploaded: {form.video_url.split('/').pop()}</p>
                  <video src={form.video_url} controls style={{ width: '100%', maxWidth: 400, borderRadius: 8, marginBottom: 8 }} />
                  <br />
                  <button type="button" className="btn-sm btn-edit" onClick={() => setForm(prev => ({ ...prev, video_url: '' }))}>Upload ulang</button>
                </div>
              ) : (
                <div>
                  <label className="form-label">Upload Video File (Max 500MB)</label>
                  <div style={{fontSize:10,color:'#f59e0b',marginBottom:6,padding:'6px 10px',background:'rgba(245,158,11,0.06)',borderRadius:6,lineHeight:1.6}}>
                    🎬 <strong>Format:</strong> MP4, WebM, AVI, MOV &nbsp;|&nbsp; <strong>Max:</strong> 500MB<br/>
                    📐 <strong>Resolusi:</strong> 1920×1080 (1080p) atau 1280×720 (720p) — Rasio 16:9
                  </div>
                  <input type="file" accept="video/mp4,video/webm,video/avi,video/quicktime" onChange={handleFileUpload} className="form-input" disabled={uploading} />
                  {uploading && (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ width: '100%', height: 6, background: 'rgba(255,255,255,0.06)', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ width: `${uploadProgress}%`, height: '100%', background: 'linear-gradient(135deg, #f59e0b, #ef4444)', borderRadius: 3, transition: 'width 0.3s' }} />
                      </div>
                      <p style={{ fontSize: 12, color: '#f59e0b', marginTop: 4 }}>{uploadProgress}% uploading...</p>
                    </div>
                  )}
                  <p style={{ fontSize: 11, color: '#555', marginTop: 6 }}>Format: MP4, WebM, AVI, MOV</p>
                </div>
              )}
            </div>

            {/* Thumbnail */}
            <div style={{ marginTop: 12 }}>
              <label className="form-label">Thumbnail</label>
              <div style={{fontSize:10,color:'#f59e0b',marginBottom:4,padding:'6px 10px',background:'rgba(245,158,11,0.06)',borderRadius:6,lineHeight:1.6}}>
                🖼️ <strong>Ukuran:</strong> 640×360px (16:9) &nbsp;|&nbsp; <strong>Format:</strong> JPG, PNG, WebP &nbsp;|&nbsp; <strong>Max:</strong> 2MB
              </div>
              <input type="file" accept="image/*" onChange={handleThumbUpload} className="form-input" />
              {form.thumbnail && <img src={form.thumbnail.startsWith('http') ? form.thumbnail : form.thumbnail} alt="thumb" style={{ width: 120, height: 68, objectFit: 'cover', borderRadius: 6, marginTop: 6 }} />}
            </div>

            {/* Video Details */}
            <div className="two-col" style={{ marginTop: 12 }}>
              <div>
                <label className="form-label">Title *</label>
                <input className="form-input" value={form.title} onChange={e => setForm({...form, title: e.target.value})} required />
              </div>
              <div>
                <label className="form-label">Category</label>
                <select className="form-select" value={form.category} onChange={e => setForm({...form, category: e.target.value})}>
                  <option value="">Select category</option>
                  {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label">Quality</label>
                <select className="form-select" value={form.quality} onChange={e => setForm({...form, quality: e.target.value})}>
                  {['720p','1080p','1440p','4K'].map(q => <option key={q} value={q}>{q}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label">Duration</label>
                <input className="form-input" value={form.duration} onChange={e => setForm({...form, duration: e.target.value})} placeholder="e.g. 12:34" />
              </div>
              <div>
                <label className="form-label">Uploader / Channel Name</label>
                <input className="form-input" value={form.uploader} onChange={e => setForm({...form, uploader: e.target.value})} placeholder="e.g. Raja Bokep Official" />
              </div>
            </div>
            <div>
            <div className="two-col">
              <div className="toggle-wrap">
                <button type="button" className={`toggle-switch ${form.is_new ? "active" : ""}`} onClick={() => setForm({...form, is_new: !form.is_new})} />
                <span className="toggle-label">Label: Bokep Terbaru</span>
              </div>
              <div className="toggle-wrap">
                <button type="button" className={`toggle-switch ${form.is_trending ? "active" : ""}`} onClick={() => setForm({...form, is_trending: !form.is_trending})} />
                <span className="toggle-label">Label: Trending</span>
              </div>
            </div>
              <label className="form-label">Tags (comma separated)</label>
              <input className="form-input" value={form.tags} onChange={e => setForm({...form, tags: e.target.value})} placeholder="japan, hot, trending" />
            </div>
            <div>
              <label className="form-label">Description</label>
              <textarea className="form-textarea" value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3} />
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button type="submit" className="btn-primary"><Save size={16} /> {editing ? 'Update' : 'Save Video'}</button>
              <button type="button" className="btn-secondary" onClick={resetForm}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Video List */}
      <div className="admin-card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="admin-table">
          <thead>
            <tr><th>Thumb</th><th>Title</th><th>Category</th><th>Quality</th><th>Views</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {videos.length === 0 ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: 30, color: '#555' }}>No videos yet. Click "Add Video" to upload!</td></tr>
            ) : videos.map(v => (
              <tr key={v.id}>
                <td><img src={v.thumbnail || '/logo.png'} alt="" style={{ width: 80, height: 45, objectFit: 'cover', borderRadius: 4 }} /></td>
                <td style={{ fontWeight: 600, color: '#ddd', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.title}</td>
                <td><span className="badge badge-info">{v.category || '-'}</span></td>
                <td><span className="badge badge-warning">{v.quality}</span></td>
                <td>{(v.views || 0).toLocaleString()}</td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button className="btn-sm btn-edit" onClick={() => handleEdit(v)}><Pencil size={12} /></button>
                    <button className="btn-sm btn-delete" onClick={() => handleDelete(v.id)}><Trash2 size={12} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default VideoManager;
